import { useMemo } from 'react';
import { GridService } from '../services/grid.service';
import { useStore, useStorePrimitive } from './useStore';
import type { RenderedImageRect } from '../types/types';
import { logger } from '../services/logger.client';
import { DEFAULT_GRID_SETTINGS } from '../types/types';

/**
 * Provides a memoized, configured instance of the GridService.
 * This hook ensures that the GridService is always in sync with the
 * current grid settings from the global state.
 *
 * @returns A configured GridService instance.
 */
export function useGridService(imageRect: RenderedImageRect | null): GridService {
  const activeMapId = useStorePrimitive(state => state.session?.map?.activeMapId);
  const allGridSettings = useStore(state => state.settings?.grids);

  const gridSettings = useMemo(() => {
    if (activeMapId == null) return DEFAULT_GRID_SETTINGS;
    return allGridSettings?.[activeMapId.toString()] ?? DEFAULT_GRID_SETTINGS;
  }, [allGridSettings, activeMapId]);

  const gridOrigin = useMemo(() => {
    if (!imageRect) return { x: 0, y: 0 };
    return {
      x: imageRect.x + (gridSettings.offsetX || 0),
      y: imageRect.y + (gridSettings.offsetY || 0),
    };
  }, [imageRect, gridSettings.offsetX, gridSettings.offsetY]);

  const gridService = useMemo(() => {
    logger.info('[useGridService] Creating and configuring new GridService instance.');
    const service = new GridService();
    
    if (gridSettings.enabled && gridSettings.size > 0 && imageRect) {
      service.configure({
        orientation: 'pointy', // Assuming pointy-top hexes
        size: gridSettings.size,
        offset: -1, // odd-q
        origin: gridOrigin,
      });
    }
    return service;
  }, [gridSettings, gridOrigin, imageRect]);

  return gridService;
}